import React, { useMemo, useState, useEffect } from "react";
import { Line } from "react-chartjs-2";
import { Campaign } from "../../../services";

interface Redemption {
  id: string;
  date: string;
  fullName: string;
  phone: string;
  codes: string[];
  campaign: string;
  status: "completed" | "pending" | "cancelled";
  totalValue: number;
  winCount: number;
}

interface RedemptionLineChartProps {
  redemptions: Redemption[];
  campaigns: Campaign[];
  selectedCampaign?: string;
  onCampaignChange?: (campaign: string) => void;
}

const RedemptionLineChart: React.FC<RedemptionLineChartProps> = ({
  redemptions,
  campaigns,
  selectedCampaign = "all",
  onCampaignChange,
}) => {
  const [dateRange, setDateRange] = useState<7 | 30 | 90>(30);
  const [countMode, setCountMode] = useState<"unique" | "total">("unique");
  const [isDarkMode, setIsDarkMode] = useState(false);

  // Detect dark mode
  useEffect(() => {
    const checkDarkMode = () => {
      setIsDarkMode(document.documentElement.classList.contains('dark'));
    };
    
    checkDarkMode();
    const observer = new MutationObserver(checkDarkMode);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    });
    
    return () => observer.disconnect();
  }, []);

  // Filter redemptions by selected campaign
  const filteredRedemptions = useMemo(() => {
    if (selectedCampaign === "all") {
      return redemptions;
    }
    return redemptions.filter(
      (redemption) => redemption.campaign === selectedCampaign
    );
  }, [redemptions, selectedCampaign]);

  // Process data for chart
  const chartData = useMemo(() => {
    const now = new Date();
    const startDate = new Date(now.getTime() - dateRange * 24 * 60 * 60 * 1000);

    // Group redemptions by date - count unique winners per day
    const dailyWinners = new Map<string, Set<string>>();

    // Initialize all dates in range
    for (let d = new Date(startDate); d <= now; d.setDate(d.getDate() + 1)) {
      const dateStr = d.toISOString().split("T")[0];
      dailyWinners.set(dateStr, new Set());
    }

    // Count winners per day based on mode
    const dailyCounts = new Map<string, number>();

    filteredRedemptions.forEach((redemption) => {
      const redemptionDate = new Date(redemption.date);
      if (redemptionDate >= startDate && redemptionDate <= now) {
        const dateStr = redemptionDate.toISOString().split("T")[0];

        if (countMode === "unique") {
          const winnersSet = dailyWinners.get(dateStr);
          if (winnersSet) {
            winnersSet.add(redemption.phone);
          }
        } else {
          // Count total redemptions
          dailyCounts.set(dateStr, (dailyCounts.get(dateStr) || 0) + 1);
        }
      }
    });

    // Calculate remaining rewards for each day
    let cumulativeUsed = 0;
    const labels = Array.from(dailyWinners.keys()).map((date) => {
      const d = new Date(date);
      return d.toLocaleDateString("vi-VN", {
        month: "short",
        day: "numeric",
      });
    });

    const winnerCounts =
      countMode === "unique"
        ? Array.from(dailyWinners.values()).map((winners) => winners.size)
        : Array.from(dailyWinners.keys()).map(
            (date) => dailyCounts.get(date) || 0
          );
    // Calculate remaining rewards (assuming a fixed total for demonstration)
    const totalRewards =
      selectedCampaign === "all"
        ? campaigns.reduce((sum, c) => sum + (c.total_gift || 0), 0)
        : campaigns.find((c) => c.name === selectedCampaign)?.total_gift || 0;

    const remainingCounts = Array.from(dailyWinners.keys()).map(
      (date, index) => {
        const usedToday = winnerCounts[index];
        cumulativeUsed += usedToday;
        return Math.max(0, totalRewards - cumulativeUsed);
      }
    );

    return { labels, winnerCounts, remainingCounts };
  }, [filteredRedemptions, dateRange, campaigns, selectedCampaign, countMode]);

  const lineChartData = {
    labels: chartData.labels,
    datasets: [
      {
        label: countMode === "unique" ? "Số người trúng" : "Số lượt trúng",
        data: chartData.winnerCounts,
        borderColor:
          countMode === "unique"
            ? "rgba(34, 197, 94, 1)"
            : "rgba(239, 68, 68, 1)",
        backgroundColor:
          countMode === "unique"
            ? "rgba(34, 197, 94, 0.1)"
            : "rgba(239, 68, 68, 0.1)",
        tension: 0.4,
        fill: true,
        spanGaps: false,
        pointBackgroundColor:
          countMode === "unique"
            ? "rgba(34, 197, 94, 1)"
            : "rgba(239, 68, 68, 1)",
        pointBorderColor: "#fff",
        pointBorderWidth: 2,
        pointRadius: 4,
        pointHoverRadius: 6,
        pointHoverBackgroundColor:
          countMode === "unique"
            ? "rgba(34, 197, 94, 1)"
            : "rgba(239, 68, 68, 1)",
        pointHoverBorderColor: "#fff",
        pointHoverBorderWidth: 3,
      },
      // Only show remaining gifts dataset when in "số lượt" mode
      ...(countMode === "total" ? [{
        label: "Chưa sử dụng",
        data: chartData.remainingCounts,
        borderColor: "rgba(59, 130, 246, 1)",
        backgroundColor: "rgba(59, 130, 246, 0.1)",
        tension: 0.4,
        fill: "+1",
        spanGaps: false,
        pointBackgroundColor: "rgba(59, 130, 246, 1)",
        pointBorderColor: "#fff",
        pointBorderWidth: 2,
        pointRadius: 4,
        pointHoverRadius: 6,
        pointHoverBackgroundColor: "rgba(59, 130, 246, 1)",
        pointHoverBorderColor: "#fff",
        pointHoverBorderWidth: 3,
      }] : []),
    ],
  };

  const lineChartOptions: any = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: false,
      },
      tooltip: {
        backgroundColor: isDarkMode ? "rgba(0, 0, 0, 0.9)" : "rgba(0, 0, 0, 0.8)",
        titleColor: "#fff",
        bodyColor: "#fff",
        borderColor: "rgba(34, 197, 94, 0.5)",
        borderWidth: 1,
        cornerRadius: 8,
        mode: "index",
        intersect: false,
        callbacks: {
          title: (context: any) => {
            const originalDate = Array.from(
              new Map(
                chartData.labels.map((label, index) => {
                  const now = new Date();
                  const startDate = new Date(
                    now.getTime() - dateRange * 24 * 60 * 60 * 1000
                  );
                  const date = new Date(
                    startDate.getTime() + index * 24 * 60 * 60 * 1000
                  );
                  return [label, date.toLocaleDateString("vi-VN")];
                })
              )
            ).find(([label]) => label === context[0].label)?.[1];
            return originalDate || context[0].label;
          },
          label: (context: any) => {
            if (context.datasetIndex === 0) {
              return countMode === "unique" 
                ? `Số người trúng: ${context.parsed.y}`
                : `Số lượt trúng: ${context.parsed.y}`;
            } else {
              return `Số quà còn lại: ${context.parsed.y}`;
            }
          },
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: "Số lượng",
          font: {
            size: 14,
            weight: "600",
          },
          color: isDarkMode ? "#f3f4f6" : "#374151",
        },
        grid: {
          color: isDarkMode ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)",
        },
        ticks: {
          stepSize: 1,
          callback: (value: any) => (Number.isInteger(value) ? value : null),
          color: isDarkMode ? "#d1d5db" : "#6b7280",
        },
      },
      x: {
        title: {
          display: true,
          text: "Ngày",
          font: {
            size: 14,
            weight: "600",
          },
          color: isDarkMode ? "#f3f4f6" : "#374151",
        },
        grid: {
          display: false,
        },
        ticks: {
          maxTicksLimit: 10,
          color: isDarkMode ? "#d1d5db" : "#6b7280",
        },
      },
    },
    interaction: {
      mode: "nearest",
      axis: "x",
      intersect: false,
    },
    animation: {
      duration: 2000,
      easing: "easeInOutQuart",
    },
  }), [isDarkMode, chartData.labels, dateRange, countMode]);

  return (
    <div className="bg-white dark:bg-gray-800 px-4 py-4 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm">
      {/* Title and Legend */}
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-bold text-gray-800 dark:text-white">
          Thống kê số người trúng {dateRange} ngày gần đây
        </h3>
        <div className="flex items-center gap-4">
          {/* Count Mode Toggle */}
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600 dark:text-gray-300 font-medium">
              Chế độ đếm:
            </label>
            <div className="flex bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
              <button
                onClick={() => setCountMode("unique")}
                className={`px-3 py-1 rounded-md text-sm transition-colors ${
                  countMode === "unique"
                    ? "bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm"
                    : "text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
                }`}
              >
                Số người
              </button>
              <button
                onClick={() => setCountMode("total")}
                className={`px-3 py-1 rounded-md text-sm transition-colors ${
                  countMode === "total"
                    ? "bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm"
                    : "text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
                }`}
              >
                Số lượt
              </button>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600 dark:text-gray-300 font-medium">
              Chiến dịch:
            </label>
            <select
              value={selectedCampaign}
              onChange={(e) => onCampaignChange?.(e.target.value)}
              className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-green-500 min-w-[200px]"
            >
              <option value="all">Tất cả chiến dịch</option>
              {campaigns.map((campaign) => (
                <option key={campaign._id} value={campaign.name}>
                  {campaign.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="flex items-center justify-between mb-6">
        {/* Summary Stats - Left */}
        <div className="text-center">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div className="text-gray-600 dark:text-gray-300">
              {countMode === "unique"
                ? "Tổng số người trúng:"
                : "Tổng số lượt trúng:"}{" "}
              <span className="font-semibold text-green-600 dark:text-green-400">
                {countMode === "unique"
                  ? new Set(filteredRedemptions.map((r) => r.phone)).size
                  : filteredRedemptions.length}
              </span>
            </div>
            <div className="text-gray-600 dark:text-gray-300">
              Trung bình/ngày:{" "}
              <span className="font-semibold text-green-600 dark:text-green-400">
                {dateRange > 0
                  ? Math.round(
                      ((countMode === "unique"
                        ? new Set(filteredRedemptions.map((r) => r.phone)).size
                        : filteredRedemptions.length) /
                        dateRange) *
                        10
                    ) / 10
                  : 0}
              </span>
            </div>
          </div>
        </div>

        {/* Date Range Selection - Center */}
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600 dark:text-gray-300 font-medium">
              Khoảng thời gian:
            </label>
            <div className="flex gap-1">
              {[7, 30, 90].map((days) => (
                <button
                  key={days}
                  onClick={() => setDateRange(days as 7 | 30 | 90)}
                  className={`px-3 py-1 rounded-md text-sm border transition-colors ${
                    dateRange === days
                      ? "bg-green-600 text-white border-green-600"
                      : "bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600"
                  }`}
                >
                  {days} ngày
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <div
              className={`w-4 h-4 rounded-full ${
                countMode === "unique" ? "bg-green-500" : "bg-red-500"
              }`}
            ></div>
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {countMode === "unique" ? "Số người trúng" : "Số lượt trúng"}
            </span>
          </div>
          {/* Only show "Số quà còn lại" legend when in "số lượt" mode */}
          {countMode === "total" && (
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-blue-500 rounded-full"></div>
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Số quà còn lại
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Chart */}
      <div className="h-96">
        <Line data={lineChartData} options={lineChartOptions} />
      </div>
    </div>
  );
};

export default RedemptionLineChart;
